import { useCallback, useEffect, useRef, useState } from "react";
import { getTherapistCalendar } from "@/lib/therapist-scheduling/api";
import {
	CALENDAR_FANOUT_CONCURRENCY,
	chunkDateRange,
	type DayAvailability,
	mapCalendarDays,
	runWithConcurrency,
} from "@/lib/therapist-scheduling/schedule-board";
import type {
	ScheduleConfigInfo,
	TherapistCalendarDay,
} from "@/lib/therapist-scheduling/types";

export type TherapistBoardAvailability = {
	byDate: Record<string, DayAvailability>;
	/**
	 * Constant across the range. `max_daily_appointments` is the denominator the
	 * day cell's load meter reads against — it is the cap Go actually enforces
	 * (`dayAtCap` in manager/therapist/calendar.go), not a display convention.
	 */
	scheduleConfig?: ScheduleConfigInfo;
	contractStartDate?: string | null;
	contractEndDate?: string | null;
	/** Set when every window for this therapist failed. */
	error?: string;
};

/**
 * Fans the upstream calendar out across the therapists on the board.
 *
 * Two things this does that a per-row `useTherapistCalendar` cannot:
 *
 * - **One shared queue.** Each row's fetch is a Rails route proxying
 *   synchronously to Go, so N rows = N parked Puma threads. Rows can't cap that
 *   between themselves; the board runs every (therapist × window) request through
 *   a single bounded queue.
 * - **Range chunking.** The calendar endpoint rejects spans over 90 days while
 *   this page's date filter is free-form, so a long range becomes several
 *   windows that are merged back into one per-date map.
 *
 * Failures are per therapist: Go being down empties the availability markers on
 * that row and leaves the locally-sourced appointment list intact.
 */
export function useScheduleBoardAvailability(
	therapistIds: readonly string[],
	startDate: string | undefined,
	endDate: string | undefined,
	enabled: boolean,
) {
	const [byTherapist, setByTherapist] = useState<
		Record<string, TherapistBoardAvailability>
	>({});
	const [isLoading, setIsLoading] = useState(false);
	const controllerRef = useRef<AbortController | null>(null);
	// Serialized so the effect re-runs on a real change, not on a new array identity.
	const key = JSON.stringify({ therapistIds, startDate, endDate, enabled });

	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized params
	const run = useCallback(() => {
		controllerRef.current?.abort();
		if (!enabled || !startDate || !endDate || therapistIds.length === 0) {
			setByTherapist({});
			setIsLoading(false);
			return;
		}

		const controller = new AbortController();
		controllerRef.current = controller;
		const windows = chunkDateRange(startDate, endDate);

		// kept flat and parallel to `tasks` so a rejected result — which carries no
		// payload — can still be attributed to the therapist it was fetched for
		const owners = therapistIds.flatMap((therapistId) =>
			windows.map(() => therapistId),
		);
		const tasks = therapistIds.flatMap((therapistId) =>
			windows.map(
				(window) => () =>
					getTherapistCalendar(
						therapistId,
						{ ...window, bypass_contract_check: true },
						{ signal: controller.signal },
					),
			),
		);

		setIsLoading(true);
		runWithConcurrency(tasks, CALENDAR_FANOUT_CONCURRENCY)
			.then((results) => {
				if (controller.signal.aborted) return;

				const days: Record<string, TherapistCalendarDay[]> = {};
				const next: Record<string, TherapistBoardAvailability> = {};
				for (const id of therapistIds) {
					days[id] = [];
					next[id] = { byDate: {} };
				}

				const failures: Record<string, number> = {};
				results.forEach((result, index) => {
					const therapistId = owners[index];
					if (result.status === "rejected") {
						failures[therapistId] = (failures[therapistId] ?? 0) + 1;
						return;
					}
					const { data } = result.value;
					days[therapistId].push(...(data.calendar ?? []));
					next[therapistId].scheduleConfig ??= data.schedule_config;
					next[therapistId].contractStartDate ??= data.contract_start_date;
					next[therapistId].contractEndDate ??= data.contract_end_date;
				});

				for (const id of therapistIds) {
					next[id].byDate = mapCalendarDays(days[id]);
					// Only a total failure is reported: a partial one still has real days
					// to show, and blanking them would hide more than it explains.
					// `windows.length > 0` matters: an inverted range chunks to nothing, and
					// "0 of 0 windows failed" must not read as a total failure
					if (windows.length > 0 && failures[id] === windows.length) {
						next[id].error = "unavailable";
					}
				}
				setByTherapist(next);
			})
			.finally(() => {
				if (controllerRef.current === controller) setIsLoading(false);
			});
	}, [key]);

	useEffect(() => {
		run();
		return () => controllerRef.current?.abort();
	}, [run]);

	return { byTherapist, isLoading };
}
